import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queensApi, type QueenBatchAssignment, type QueenBatchDetail, type QueenBatchListItem, type QueenMatingNuc, type QueenMetaHive, type QueenMetaSite } from '../services/queens';

const inputClass = 'w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 shadow-sm outline-none transition focus:border-amber-500';
const saveBtnClass = 'inline-flex items-center rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60';
const secondaryBtnClass = 'inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60';

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDate(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('sv-SE');
}

function addDays(dateStr: string, days: number) {
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '-';
  date.setDate(date.getDate() + days);
  return date.toLocaleDateString('sv-SE');
}

function addDaysIso(dateStr: string, days: number) {
  const date = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatWeekday(dateStr: string | null) {
  if (!dateStr) return '-';
  const date = new Date(`${dateStr}T12:00:00`);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('sv-SE', { weekday: 'long' });
}

function takePercent(takeCount: number | null | undefined, queenCount: number | null | undefined) {
  if (takeCount == null || !queenCount || queenCount < 1) return null;
  return Math.round((takeCount / queenCount) * 1000) / 10;
}

function hiveLabel(hive?: { hiveNo: number | null; name: string | null; apiaryName?: string | null }) {
  if (!hive) return '-';
  const base = hive.hiveNo != null ? `Kupa #${hive.hiveNo}` : (hive.name || 'Kupa');
  return hive.apiaryName ? `${base} · ${hive.apiaryName}` : base;
}

function siteLabel(site: QueenMetaSite) {
  return [site.name, site.parentName, site.siteTypeLabel].filter(Boolean).join(' · ');
}

export function QueensPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const canWriteQueens = canWrite('queens');

  const [loading, setLoading] = useState(true);
  const [savingBatch, setSavingBatch] = useState(false);
  const [editingBatch, setEditingBatch] = useState(false);
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [savingTake, setSavingTake] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [savingNuc, setSavingNuc] = useState(false);
  const [metaHives, setMetaHives] = useState<QueenMetaHive[]>([]);
  const [metaSites, setMetaSites] = useState<QueenMetaSite[]>([]);
  const [batches, setBatches] = useState<QueenBatchListItem[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<QueenBatchDetail | null>(null);
  const [assignments, setAssignments] = useState<QueenBatchAssignment[]>([]);
  const [matingNucs, setMatingNucs] = useState<QueenMatingNuc[]>([]);
  const [batchForm, setBatchForm] = useState({ graftedAt: todayStr(), motherHiveSiteId: '', queenCount: '12', notes: '' });
  const [batchEditForm, setBatchEditForm] = useState({ graftedAt: '', motherHiveSiteId: '', queenCount: '', notes: '' });
  const [takeForm, setTakeForm] = useState('');
  const [assignmentForm, setAssignmentForm] = useState({ targetType: 'HIVE' as 'HIVE' | 'MATING_NUC', targetHiveSiteId: '', targetMatingNucId: '', assignedAt: todayStr(), notes: '' });
  const [nucForm, setNucForm] = useState({ nucNo: '', label: '', locationSiteId: '', notes: '' });
  const [editingNucId, setEditingNucId] = useState<number | null>(null);
  const [editingNucForm, setEditingNucForm] = useState({ nucNo: '', label: '', locationSiteId: '', notes: '', isActive: true });

  async function loadBase() {
    setLoading(true);
    try {
      const [metaRes, batchRes, nucRes] = await Promise.all([queensApi.meta(), queensApi.batches.list(), queensApi.matingNucs.list()]);
      setMetaHives(metaRes.hives);
      setMetaSites(metaRes.sites);
      setBatches(batchRes.items);
      setMatingNucs(nucRes.items);
      setSelectedBatchId((current) => current ?? batchRes.items[0]?.id ?? null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda drottningodling');
    } finally {
      setLoading(false);
    }
  }

  async function loadSelectedBatch(batchId: number) {
    try {
      const data = await queensApi.batches.get(batchId);
      setSelectedBatch(data.batch);
      setAssignments(data.assignments);
      setBatchEditForm({
        graftedAt: data.batch.graftedAt,
        motherHiveSiteId: String(data.batch.motherHiveSiteId),
        queenCount: String(data.batch.queenCount),
        notes: data.batch.notes || '',
      });
      setTakeForm(data.batch.takeCount == null ? '' : String(data.batch.takeCount));
      setEditingBatch(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda batchen');
      setSelectedBatch(null);
      setAssignments([]);
    }
  }

  useEffect(() => {
    void loadBase();
  }, []);

  useEffect(() => {
    if (selectedBatchId != null) {
      void loadSelectedBatch(selectedBatchId);
    }
  }, [selectedBatchId]);

  const availableTargetHives = useMemo(() => metaHives.filter((hive) => hive.siteId !== selectedBatch?.motherHiveSiteId), [metaHives, selectedBatch?.motherHiveSiteId]);

  async function handleCreateBatch(event: React.FormEvent) {
    event.preventDefault();
    if (!canWriteQueens || savingBatch) return;
    const motherHiveSiteId = Number(batchForm.motherHiveSiteId);
    const queenCount = Number(batchForm.queenCount);
    if (!motherHiveSiteId) return toast.error('Välj modersamhälle');
    if (!queenCount || queenCount < 1) return toast.error('Antal måste vara minst 1');
    setSavingBatch(true);
    try {
      const created = await queensApi.batches.create({ graftedAt: batchForm.graftedAt, motherHiveSiteId, queenCount, notes: batchForm.notes.trim() || undefined });
      toast.success(`Batch ${created.batchCode} skapad`);
      setBatchForm({ graftedAt: todayStr(), motherHiveSiteId: '', queenCount: '12', notes: '' });
      setShowCreateBatch(false);
      await loadBase();
      setSelectedBatchId(created.id);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa batch');
    } finally {
      setSavingBatch(false);
    }
  }

  async function handleUpdateBatch(event: React.FormEvent) {
    event.preventDefault();
    if (!canWriteQueens || savingBatch || !selectedBatch) return;
    const motherHiveSiteId = Number(batchEditForm.motherHiveSiteId);
    const queenCount = Number(batchEditForm.queenCount);
    if (!batchEditForm.graftedAt) return toast.error('Larvningsdatum krävs');
    if (!motherHiveSiteId) return toast.error('Välj modersamhälle');
    if (!queenCount || queenCount < 1) return toast.error('Antal måste vara minst 1');
    setSavingBatch(true);
    try {
      await queensApi.batches.update(selectedBatch.id, {
        graftedAt: batchEditForm.graftedAt,
        motherHiveSiteId,
        queenCount,
        takeCount: selectedBatch.takeCount,
        notes: batchEditForm.notes.trim() || undefined,
      });
      toast.success('Batchen uppdaterad');
      await Promise.all([loadBase(), loadSelectedBatch(selectedBatch.id)]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera batchen');
    } finally {
      setSavingBatch(false);
    }
  }

  async function handleSaveTake() {
    if (!canWriteQueens || savingTake || !selectedBatch) return;
    const takeCount = takeForm.trim() === '' ? null : Number(takeForm);
    if (takeCount !== null && (!Number.isInteger(takeCount) || takeCount < 0 || takeCount > selectedBatch.queenCount)) {
      return toast.error('Take måste vara mellan 0 och antal larver');
    }
    setSavingTake(true);
    try {
      await queensApi.batches.update(selectedBatch.id, {
        graftedAt: selectedBatch.graftedAt,
        motherHiveSiteId: selectedBatch.motherHiveSiteId,
        queenCount: selectedBatch.queenCount,
        takeCount,
        notes: selectedBatch.notes || undefined,
      });
      toast.success('Take sparad');
      await Promise.all([loadBase(), loadSelectedBatch(selectedBatch.id)]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte spara take');
    } finally {
      setSavingTake(false);
    }
  }

  async function handleAssign(event: React.FormEvent) {
    event.preventDefault();
    if (!canWriteQueens || savingAssignment || !selectedBatch) return;
    const targetHiveSiteId = Number(assignmentForm.targetHiveSiteId);
    const targetMatingNucId = Number(assignmentForm.targetMatingNucId);
    if (assignmentForm.targetType === 'HIVE' && !targetHiveSiteId) return toast.error('Välj kupa');
    if (assignmentForm.targetType === 'MATING_NUC' && !targetMatingNucId) return toast.error('Välj parningskupa');
    setSavingAssignment(true);
    try {
      await queensApi.assignments.create({
        queenBatchId: selectedBatch.id,
        targetType: assignmentForm.targetType,
        targetHiveSiteId: assignmentForm.targetType === 'HIVE' ? targetHiveSiteId : undefined,
        targetMatingNucId: assignmentForm.targetType === 'MATING_NUC' ? targetMatingNucId : undefined,
        assignedAt: assignmentForm.assignedAt,
        notes: assignmentForm.notes.trim() || undefined,
      });
      toast.success('Kopplingen sparad');
      setAssignmentForm({ targetType: 'HIVE', targetHiveSiteId: '', targetMatingNucId: '', assignedAt: todayStr(), notes: '' });
      await Promise.all([loadBase(), loadSelectedBatch(selectedBatch.id)]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte koppla batchen');
    } finally {
      setSavingAssignment(false);
    }
  }

  async function handleCreateNuc(event: React.FormEvent) {
    event.preventDefault();
    if (!canWriteQueens || savingNuc) return;
    const nucNo = Number(nucForm.nucNo);
    const locationSiteId = Number(nucForm.locationSiteId);
    if (!nucNo) return toast.error('Ange nummer');
    if (!locationSiteId) return toast.error('Välj site');
    setSavingNuc(true);
    try {
      await queensApi.matingNucs.create({ nucNo, label: nucForm.label.trim() || undefined, locationSiteId, notes: nucForm.notes.trim() || undefined });
      toast.success('Parningskupa skapad');
      setNucForm({ nucNo: '', label: '', locationSiteId: '', notes: '' });
      await loadBase();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa parningskupa');
    } finally {
      setSavingNuc(false);
    }
  }

  function startEditNuc(nuc: QueenMatingNuc) {
    setEditingNucId(nuc.id);
    setEditingNucForm({
      nucNo: String(nuc.nucNo),
      label: nuc.label || '',
      locationSiteId: nuc.locationSiteId != null ? String(nuc.locationSiteId) : '',
      notes: nuc.notes || '',
      isActive: nuc.isActive === 1,
    });
  }

  async function handleSaveEditedNuc(nucId: number) {
    if (!canWriteQueens || savingNuc) return;
    const nucNo = Number(editingNucForm.nucNo);
    const locationSiteId = Number(editingNucForm.locationSiteId);
    if (!nucNo || !locationSiteId) return toast.error('Nummer och site krävs');
    setSavingNuc(true);
    try {
      await queensApi.matingNucs.update(nucId, {
        nucNo,
        label: editingNucForm.label.trim() || undefined,
        locationSiteId,
        notes: editingNucForm.notes.trim() || undefined,
        isActive: editingNucForm.isActive,
      });
      toast.success('Parningskupan uppdaterad');
      setEditingNucId(null);
      await loadBase();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte uppdatera parningskupan');
    } finally {
      setSavingNuc(false);
    }
  }

  const batchCalendarRows = useMemo(() => {
    if (!selectedBatch) return [];
    return [
      { day: 1, dateOffset: -1, title: 'Skapa startersamhälle', notes: 'Mata med 1/1-sockerlösning i bottenlådan.' },
      { day: 2, dateOffset: 0, title: 'Larva', notes: 'Larva och riv ner eventuella drottningsceller i startersamhället.' },
      { day: 4, dateOffset: 2, title: 'Kontrollera take', notes: 'Räkna take och riv eventuella drottningsceller på ramar.', takeTask: true },
      { day: 10, dateOffset: 8, title: 'Inspektion av täckta celler', notes: 'Inspektera täckta celler och räkna om take.', takeTask: true },
      { day: 11, dateOffset: 9, title: 'Flytta celler till kläckskåp', notes: 'Valfritt: flytta täckta celler till kläckskåp om de ska kläckas där.' },
      { day: 13, dateOffset: 11, title: 'Skapa avläggare', notes: 'Skapa avläggare efter antal byggda drottningceller.' },
      { day: 14, dateOffset: 12, title: 'Flytta celler till avläggare', notes: 'Flytta cellerna försiktigt till avläggarna.' },
      { day: 15, dateOffset: 13, title: 'Kläckning', notes: 'Beräknad kläckning. Stör inte avläggarna i onödan.' },
    ].map((row) => {
      const date = addDaysIso(selectedBatch.graftedAt, row.dateOffset);
      return { ...row, date, weekday: formatWeekday(date) };
    });
  }, [selectedBatch]);

  const takeFormPercent = selectedBatch && takeForm.trim() !== '' && Number(selectedBatch.queenCount) > 0
    ? takePercent(Number(takeForm), selectedBatch.queenCount)
    : null;

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <Link to="/" className="inline-flex items-center rounded-2xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800 shadow-sm transition hover:border-slate-400 hover:bg-slate-50">Tillbaka</Link>
        <div className="space-y-1">
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950 md:text-[30px]">Drottningodling</h1>
          <p className="text-sm text-slate-600">Larvningsbatcher, kläckningskalender, parningskupor och koppling till samhällen.</p>
        </div>
      </div>

      {canWriteQueens ? (
        <div className="flex justify-end">
          <button type="button" className={saveBtnClass} onClick={() => setShowCreateBatch((current) => !current)}>
            {showCreateBatch ? 'Stäng' : 'Skapa ny batch'}
          </button>
        </div>
      ) : null}

      {showCreateBatch ? (
        <form onSubmit={handleCreateBatch} className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="mb-4 text-lg font-semibold text-slate-950">Ny larvningsbatch</div>
          <div className="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)_120px]">
            <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Larvningsdatum</div><input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={batchForm.graftedAt} onChange={(event) => setBatchForm((current) => ({ ...current, graftedAt: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
            <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Modersamhälle</div><select value={batchForm.motherHiveSiteId} onChange={(event) => setBatchForm((current) => ({ ...current, motherHiveSiteId: event.target.value }))} className={inputClass} disabled={!canWriteQueens}><option value="">Välj samhälle</option>{metaHives.map((hive) => <option key={hive.siteId} value={hive.siteId}>{hiveLabel(hive)}</option>)}</select></label>
            <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Antal</div><input type="number" min={1} value={batchForm.queenCount} onChange={(event) => setBatchForm((current) => ({ ...current, queenCount: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
          </div>
          <label className="mt-4 block space-y-2"><div className="text-sm font-semibold text-slate-700">Anteckning</div><textarea value={batchForm.notes} onChange={(event) => setBatchForm((current) => ({ ...current, notes: event.target.value }))} rows={2} className={inputClass} disabled={!canWriteQueens} /></label>
          <div className="mt-5 flex justify-end gap-2">
            <button type="button" className={secondaryBtnClass} onClick={() => setShowCreateBatch(false)} disabled={savingBatch}>Avbryt</button>
            <button type="submit" disabled={savingBatch} className={saveBtnClass}>{savingBatch ? 'Sparar...' : 'Skapa batch'}</button>
          </div>
        </form>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
        <div className="overflow-hidden rounded-[28px] border border-slate-300 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="border-b border-slate-200 px-5 py-4 text-lg font-semibold text-slate-950">Batcher</div>
          <div className="max-h-[720px] overflow-auto">
            {loading ? <div className="p-5 text-sm text-slate-500">Laddar batcher...</div> : batches.length === 0 ? <div className="p-5 text-sm text-slate-500">Inga batcher ännu.</div> : (
              <div className="divide-y divide-slate-200">
                {batches.map((batch) => (
                  <button key={batch.id} type="button" onClick={() => setSelectedBatchId(batch.id)} className={`w-full px-5 py-4 text-left transition ${selectedBatchId === batch.id ? 'bg-amber-50' : 'bg-white hover:bg-slate-50'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div><div className="text-base font-semibold text-slate-950">{batch.batchCode}</div><div className="text-sm text-slate-600">{batch.graftedAt}</div></div>
                      <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{batch.assignmentCount} kopplingar</div>
                    </div>
                    <div className="mt-2 text-sm text-slate-800">{hiveLabel({ hiveNo: batch.motherHiveNo, name: batch.motherHiveName, apiaryName: batch.motherApiaryName })}</div>
                    <div className="mt-1 text-sm text-slate-600">
                      {batch.queenCount} larver
                      {batch.takeCount != null ? ` · take ${batch.takeCount} (${takePercent(batch.takeCount, batch.queenCount)}%)` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            {!selectedBatch ? <div className="text-sm text-slate-500">Välj en batch i listan.</div> : (
              <div className="space-y-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div><div className="text-[26px] font-semibold tracking-[-0.03em] text-slate-950">{selectedBatch.batchCode}</div><div className="mt-1 text-sm text-slate-600">{hiveLabel({ hiveNo: selectedBatch.motherHiveNo, name: selectedBatch.motherHiveName, apiaryName: selectedBatch.motherApiaryName })}</div></div>
                  <div className="flex flex-wrap gap-2">
                    <div className="rounded-2xl bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">{selectedBatch.queenCount} larver</div>
                    {selectedBatch.takeCount != null ? <div className="rounded-2xl bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800">Take {selectedBatch.takeCount} ({takePercent(selectedBatch.takeCount, selectedBatch.queenCount)}%)</div> : null}
                    {canWriteQueens ? <button type="button" className={secondaryBtnClass} onClick={() => setEditingBatch((current) => !current)}>{editingBatch ? 'Stäng redigering' : 'Redigera batch'}</button> : null}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Larvat</div><div className="mt-2 text-lg font-semibold text-slate-950">{selectedBatch.graftedAt}</div></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Täckt</div><div className="mt-2 text-lg font-semibold text-slate-950">{addDays(selectedBatch.graftedAt, 8)}</div></div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Beräknad kläckning</div><div className="mt-2 text-lg font-semibold text-slate-950">{addDays(selectedBatch.graftedAt, 13)}</div></div>
                </div>
                {selectedBatch.notes ? <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-700">{selectedBatch.notes}</div> : null}

                {editingBatch ? (
                  <form onSubmit={handleUpdateBatch} className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-4 text-base font-semibold text-slate-950">Redigera batch</div>
                    <div className="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)_120px_120px]">
                      <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Larvningsdatum</div><input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={batchEditForm.graftedAt} onChange={(event) => setBatchEditForm((current) => ({ ...current, graftedAt: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
                      <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Modersamhälle</div><select value={batchEditForm.motherHiveSiteId} onChange={(event) => setBatchEditForm((current) => ({ ...current, motherHiveSiteId: event.target.value }))} className={inputClass} disabled={!canWriteQueens}><option value="">Välj samhälle</option>{metaHives.map((hive) => <option key={hive.siteId} value={hive.siteId}>{hiveLabel(hive)}</option>)}</select></label>
                      <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Antal larver</div><input type="number" min={1} value={batchEditForm.queenCount} onChange={(event) => setBatchEditForm((current) => ({ ...current, queenCount: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
                    </div>
                    <label className="mt-4 block space-y-2"><div className="text-sm font-semibold text-slate-700">Anteckning</div><textarea value={batchEditForm.notes} onChange={(event) => setBatchEditForm((current) => ({ ...current, notes: event.target.value }))} rows={2} className={inputClass} disabled={!canWriteQueens} /></label>
                    <div className="mt-5 flex justify-end gap-2">
                      <button type="button" className={secondaryBtnClass} onClick={() => setEditingBatch(false)} disabled={savingBatch}>Avbryt</button>
                      <button type="submit" disabled={savingBatch} className={saveBtnClass}>{savingBatch ? 'Sparar...' : 'Spara batch'}</button>
                    </div>
                  </form>
                ) : null}

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 px-4 py-3 text-base font-semibold text-slate-950">Att göra-kalender</div>
                  <div className="max-h-[440px] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white">
                        <tr className="border-b border-slate-200 text-left text-slate-900">
                          <th className="px-3 py-2 font-semibold">Dag</th>
                          <th className="px-3 py-2 font-semibold">Veckodag</th>
                          <th className="px-3 py-2 font-semibold">Datum</th>
                          <th className="px-3 py-2 font-semibold">Uppgift/status</th>
                          <th className="px-3 py-2 font-semibold">Anteckning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {batchCalendarRows.map((row) => (
                          <tr key={`${row.day}-${row.title}`} className="border-b border-slate-200 last:border-b-0">
                            <td className="px-3 py-2 align-top font-medium text-slate-900">{row.day}</td>
                            <td className="px-3 py-2 align-top capitalize text-slate-700">{row.weekday}</td>
                            <td className="px-3 py-2 align-top text-slate-700">{row.date ? addDays(selectedBatch.graftedAt, row.dateOffset) : '-'}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="font-semibold text-slate-950">{row.title}</div>
                              {row.takeTask ? (
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  <input
                                    type="number"
                                    min={0}
                                    max={selectedBatch.queenCount}
                                    value={takeForm}
                                    onChange={(event) => setTakeForm(event.target.value)}
                                    className="w-24 rounded-xl border border-slate-300 px-3 py-2"
                                    disabled={!canWriteQueens}
                                  />
                                  <span className="text-sm text-slate-600">av {selectedBatch.queenCount}</span>
                                  {takeFormPercent != null ? <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">{takeFormPercent}%</span> : null}
                                  {canWriteQueens ? <button type="button" className={saveBtnClass} onClick={() => void handleSaveTake()} disabled={savingTake}>{savingTake ? 'Sparar...' : 'Spara take'}</button> : null}
                                </div>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 align-top text-slate-700">{row.notes}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <form onSubmit={handleAssign} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-4 text-base font-semibold text-slate-950">Koppla batch</div>
                  <div className="grid gap-4 lg:grid-cols-[180px_minmax(0,1fr)_180px]">
                    <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Typ</div><select value={assignmentForm.targetType} onChange={(event) => setAssignmentForm((current) => ({ ...current, targetType: event.target.value as 'HIVE' | 'MATING_NUC' }))} className={inputClass} disabled={!canWriteQueens}><option value="HIVE">Kupa</option><option value="MATING_NUC">Parningskupa</option></select></label>
                    {assignmentForm.targetType === 'HIVE' ? (
                      <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Kupa</div><select value={assignmentForm.targetHiveSiteId} onChange={(event) => setAssignmentForm((current) => ({ ...current, targetHiveSiteId: event.target.value }))} className={inputClass} disabled={!canWriteQueens}><option value="">Välj kupa</option>{availableTargetHives.map((hive) => <option key={hive.siteId} value={hive.siteId}>{hiveLabel(hive)}</option>)}</select></label>
                    ) : (
                      <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Parningskupa</div><select value={assignmentForm.targetMatingNucId} onChange={(event) => setAssignmentForm((current) => ({ ...current, targetMatingNucId: event.target.value }))} className={inputClass} disabled={!canWriteQueens}><option value="">Välj parningskupa</option>{matingNucs.filter((nuc) => nuc.isActive === 1).map((nuc) => <option key={nuc.id} value={nuc.id}>{`Parningskupa ${nuc.nucNo}${nuc.locationName ? ` · ${nuc.locationName}` : ''}`}</option>)}</select></label>
                    )}
                    <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Datum</div><input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" value={assignmentForm.assignedAt} onChange={(event) => setAssignmentForm((current) => ({ ...current, assignedAt: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
                  </div>
                  <label className="mt-4 block space-y-2"><div className="text-sm font-semibold text-slate-700">Anteckning</div><input value={assignmentForm.notes} onChange={(event) => setAssignmentForm((current) => ({ ...current, notes: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
                  {canWriteQueens ? <div className="mt-5 flex justify-end"><button type="submit" disabled={savingAssignment} className={saveBtnClass}>{savingAssignment ? 'Sparar...' : 'Koppla'}</button></div> : null}
                </form>

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 px-4 py-3 text-base font-semibold text-slate-950">Kopplingar</div>
                  <div className="max-h-[340px] overflow-auto">
                    <table className="min-w-full text-sm">
                      <thead className="sticky top-0 z-10 bg-white"><tr className="border-b border-slate-200 text-left text-slate-900"><th className="px-4 py-3 font-semibold">Typ</th><th className="px-4 py-3 font-semibold">Mål</th><th className="px-4 py-3 font-semibold">Datum</th><th className="px-4 py-3 font-semibold">Anteckning</th></tr></thead>
                      <tbody>
                        {assignments.length === 0 ? <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">Inga kopplingar ännu.</td></tr> : assignments.map((assignment) => (
                          <tr key={assignment.id} className="border-b border-slate-200 last:border-b-0">
                            <td className="px-4 py-3 text-slate-700">{assignment.targetType === 'HIVE' ? 'Kupa' : 'Parningskupa'}</td>
                            <td className="px-4 py-3 text-slate-900">
                              {assignment.targetType === 'HIVE' ? (assignment.targetHiveSiteId ? <Link to={`/hives/${assignment.targetHiveSiteId}`} className="font-medium text-primary hover:underline">{hiveLabel({ hiveNo: assignment.targetHiveNo, name: assignment.targetHiveName, apiaryName: assignment.targetHiveApiaryName })}</Link> : '-') : (`Parningskupa ${assignment.targetMatingNucNo ?? '-'}${assignment.targetLocationName ? ` · ${assignment.targetLocationName}` : ''}`)}
                            </td>
                            <td className="px-4 py-3 text-slate-700">{fmtDate(assignment.assignedAt)}</td>
                            <td className="px-4 py-3 text-slate-700">{assignment.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="rounded-[28px] border border-slate-300 bg-white p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
            <div className="mb-4 text-lg font-semibold text-slate-950">Parningskupor</div>
            <form onSubmit={handleCreateNuc} className="grid gap-4 border-b border-slate-200 pb-5 lg:grid-cols-[120px_160px_minmax(0,1fr)]">
              <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Nummer</div><input type="number" min={1} value={nucForm.nucNo} onChange={(event) => setNucForm((current) => ({ ...current, nucNo: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
              <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Label</div><input value={nucForm.label} onChange={(event) => setNucForm((current) => ({ ...current, label: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
              <label className="space-y-2"><div className="text-sm font-semibold text-slate-700">Site</div><select value={nucForm.locationSiteId} onChange={(event) => setNucForm((current) => ({ ...current, locationSiteId: event.target.value }))} className={inputClass} disabled={!canWriteQueens}><option value="">Välj site</option>{metaSites.map((site) => <option key={site.id} value={site.id}>{siteLabel(site)}</option>)}</select></label>
              <label className="space-y-2 lg:col-span-3"><div className="text-sm font-semibold text-slate-700">Anteckning</div><input value={nucForm.notes} onChange={(event) => setNucForm((current) => ({ ...current, notes: event.target.value }))} className={inputClass} disabled={!canWriteQueens} /></label>
              {canWriteQueens ? <div className="flex justify-end lg:col-span-3"><button type="submit" disabled={savingNuc} className={saveBtnClass}>{savingNuc ? 'Sparar...' : 'Lägg till parningskupa'}</button></div> : null}
            </form>
            <div className="mt-5 max-h-[420px] overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 z-10 bg-white"><tr className="border-b border-slate-200 text-left text-slate-900"><th className="px-3 py-3 font-semibold">Parningskupa</th><th className="px-3 py-3 font-semibold">Site</th><th className="px-3 py-3 font-semibold">Batch</th><th className="px-3 py-3 font-semibold">Aktiv</th><th className="px-3 py-3 font-semibold">Spara</th></tr></thead>
                <tbody>
                  {matingNucs.length === 0 ? <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-500">Inga parningskupor ännu.</td></tr> : matingNucs.map((nuc) => {
                    const editing = editingNucId === nuc.id;
                    return (
                      <tr key={nuc.id} className="border-b border-slate-200 last:border-b-0">
                        <td className="px-3 py-3 align-top">{editing ? <div className="space-y-2"><input type="number" min={1} value={editingNucForm.nucNo} onChange={(event) => setEditingNucForm((current) => ({ ...current, nucNo: event.target.value }))} className="w-28 rounded-xl border border-slate-300 px-3 py-2" /><input value={editingNucForm.label} onChange={(event) => setEditingNucForm((current) => ({ ...current, label: event.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2" placeholder="Label" /></div> : <div><div className="font-medium text-slate-900">{`Parningskupa ${nuc.nucNo}`}</div><div className="text-slate-600">{nuc.label || '-'}</div></div>}</td>
                        <td className="px-3 py-3 align-top">{editing ? <select value={editingNucForm.locationSiteId} onChange={(event) => setEditingNucForm((current) => ({ ...current, locationSiteId: event.target.value }))} className="w-full rounded-xl border border-slate-300 px-3 py-2"><option value="">Välj site</option>{metaSites.map((site) => <option key={site.id} value={site.id}>{siteLabel(site)}</option>)}</select> : <div className="text-slate-700">{nuc.locationName || '-'}</div>}</td>
                        <td className="px-3 py-3 align-top text-slate-700"><div>{nuc.currentQueenBatchCode || '-'}</div><div className="text-xs text-slate-500">{fmtDate(nuc.currentQueenAssignedAt)}</div></td>
                        <td className="px-3 py-3 align-top">{editing ? <label className="inline-flex items-center gap-2 text-slate-700"><input type="checkbox" checked={editingNucForm.isActive} onChange={(event) => setEditingNucForm((current) => ({ ...current, isActive: event.target.checked }))} /><span>{editingNucForm.isActive ? 'Ja' : 'Nej'}</span></label> : <span className="text-slate-700">{nuc.isActive === 1 ? 'Ja' : 'Nej'}</span>}</td>
                        <td className="px-3 py-3 align-top">{editing ? <div className="flex gap-2"><button type="button" onClick={() => void handleSaveEditedNuc(nuc.id)} disabled={savingNuc} className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">Spara</button><button type="button" onClick={() => setEditingNucId(null)} disabled={savingNuc} className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Avbryt</button></div> : canWriteQueens ? <button type="button" onClick={() => startEditNuc(nuc)} className="rounded-xl border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Redigera</button> : <span className="text-slate-400">-</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
